import type { BuildingDef } from '../data/buildings';
import type { ParkingLot } from '../models/ParkingLot';
import { CAPACITY_TILES, TILE, type TileType } from '../models/Tile';

export type PlaceResult =
  | { ok: true }
  | { ok: false; reason: 'out-of-bounds' | 'occupied' | 'insufficient-funds' };

/**
 * ParkingLotEngine (spec sections 7/8): pure grid logic for a parking lot.
 *
 * No Phaser, no I/O — it validates and mutates the plain-data grid so it can be
 * unit-tested and reused on a server. Callers (GameStore) own money and
 * broadcasting; this engine only knows about the grid and building defs.
 */
export const ParkingLotEngine = {
  index(width: number, x: number, y: number): number {
    return y * width + x;
  },

  inBounds(lot: ParkingLot, x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < lot.width && y < lot.height;
  },

  getTile(lot: ParkingLot, x: number, y: number): TileType | undefined {
    if (!this.inBounds(lot, x, y)) return undefined;
    return lot.tiles[this.index(lot.width, x, y)];
  },

  setTile(lot: ParkingLot, x: number, y: number, type: TileType): void {
    if (!this.inBounds(lot, x, y)) return;
    lot.tiles[this.index(lot.width, x, y)] = type;
  },

  /**
   * Validate placing `def` at (x, y) given how much cash the buyer has.
   * Enforces: in-bounds, no overlap (target must be Empty), affordability.
   */
  canPlace(
    lot: ParkingLot,
    x: number,
    y: number,
    def: BuildingDef,
    availableCash: number
  ): PlaceResult {
    if (!this.inBounds(lot, x, y)) return { ok: false, reason: 'out-of-bounds' };
    if (this.getTile(lot, x, y) !== TILE.Empty) {
      return { ok: false, reason: 'occupied' };
    }
    if (availableCash < def.purchaseCost) {
      return { ok: false, reason: 'insufficient-funds' };
    }
    return { ok: true };
  },

  /**
   * Place a building tile. Assumes canPlace already passed; returns the cost so
   * the caller can deduct it. Does NOT touch money itself.
   */
  place(lot: ParkingLot, x: number, y: number, def: BuildingDef): number {
    this.setTile(lot, x, y, def.tile);
    return def.purchaseCost;
  },

  /**
   * Grow the grid to a larger size, preserving existing tiles in the top-left
   * region (spec section 19). No-op / false if the new size is smaller.
   */
  expand(lot: ParkingLot, newWidth: number, newHeight: number): boolean {
    if (newWidth < lot.width || newHeight < lot.height) return false;
    if (newWidth === lot.width && newHeight === lot.height) return false;

    const tiles: TileType[] = new Array<TileType>(newWidth * newHeight).fill(
      TILE.Empty
    );
    for (let y = 0; y < lot.height; y++) {
      for (let x = 0; x < lot.width; x++) {
        tiles[y * newWidth + x] = lot.tiles[y * lot.width + x];
      }
    }
    lot.width = newWidth;
    lot.height = newHeight;
    lot.tiles = tiles;
    return true;
  },

  /** Clear a tile back to Empty. Returns true if a tile was actually removed. */
  remove(lot: ParkingLot, x: number, y: number): boolean {
    if (!this.inBounds(lot, x, y)) return false;
    if (this.getTile(lot, x, y) === TILE.Empty) return false;
    this.setTile(lot, x, y, TILE.Empty);
    return true;
  },

  /** Parking capacity = number of capacity-bearing tiles. */
  getCapacity(lot: ParkingLot): number {
    let count = 0;
    for (const tile of lot.tiles) {
      if (CAPACITY_TILES.has(tile)) count++;
    }
    return count;
  },

  /** Count tiles of a given type. */
  countTiles(lot: ParkingLot, type: TileType): number {
    let count = 0;
    for (const tile of lot.tiles) {
      if (tile === type) count++;
    }
    return count;
  },

  hasEntrance(lot: ParkingLot): boolean {
    return this.countTiles(lot, TILE.Entrance) > 0;
  },

  hasExit(lot: ParkingLot): boolean {
    return this.countTiles(lot, TILE.Exit) > 0;
  },

  /** Total monthly maintenance implied by the placed tiles (Phase 4 helper). */
  getMaintenanceCost(
    lot: ParkingLot,
    buildings: Record<string, BuildingDef>
  ): number {
    const byTile = new Map<TileType, number>();
    for (const def of Object.values(buildings)) {
      byTile.set(def.tile, def.maintenanceCost);
    }
    let total = 0;
    for (const tile of lot.tiles) {
      total += byTile.get(tile) ?? 0;
    }
    return total;
  },
};
